import { BarChart3 } from "lucide-react";
import {
  AreaChart,
  ChartCard,
  chartTitleWithBadge,
  DonutPieChart,
  formatCompact,
  RankedBarChart,
  TreeMap,
} from "../../components/ChartsComponent";
import {
  ChartEmptyState,
  ChartErrorState,
  ChartLoadingState,
} from "../../components/ChartsComponent/ChartStates";
import type { LogReviewChartKey } from "../../../hooks/useLogReviewCharts";
import type {
  AreaChartData,
  NonCompliantBreakdown,
  PieChartDatum,
  RankedBarDatum,
  TreeMapNode,
} from "../../types/logReview";
import {
  LOG_REVIEW_DONUT_PROPS,
  TEAM_HEATMAP_COLORS,
  toAreaRows,
  withDonutColors,
} from "./logReviewChartUtils";
import { NonCompliantDistributionPanel } from "./NonCompliantDistributionPanel";

const TREND_HEIGHT = 280;

export interface LogReviewChartGridProps {
  complianceStatus: PieChartDatum[];
  nonCompliantBreakdown: NonCompliantBreakdown | null;
  findingsTrend: AreaChartData;
  workflowStatus: PieChartDatum[];
  byApplications: RankedBarDatum[];
  byTeam: TreeMapNode[];
  byCrCategories: RankedBarDatum[];
  /** Per-chart loading flags — each tile shows its own skeleton independently. */
  loading?: Record<LogReviewChartKey, boolean>;
  /** Per-chart error — only the failed tile(s) flip to the error state. */
  error?: Record<LogReviewChartKey, string | null>;
  /** Re-runs the chart batch from a tile's error-state Retry button. */
  onRetry?: () => void;
}

/**
 * The seven-tile Log Review dashboard grid.
 *
 * Each tile reads its OWN loading / error slice, so a fast chart renders
 * immediately while a slow sibling keeps its skeleton, and only the failed
 * tile flips to an error state with its own Retry.
 */
export const LogReviewChartGrid = ({
  complianceStatus,
  nonCompliantBreakdown,
  findingsTrend,
  workflowStatus,
  byApplications,
  byTeam,
  byCrCategories,
  loading,
  error,
  onRetry,
}: LogReviewChartGridProps) => {
  const trend = toAreaRows(findingsTrend);

  // The distribution tile is a bespoke panel rather than a shared chart, so
  // it resolves its own non-plot states instead of delegating to one.
  const renderDistribution = () => {
    if (error?.distribution) {
      return (
        <ChartErrorState
          height={TREND_HEIGHT}
          message={error.distribution}
          onRetry={onRetry}
        />
      );
    }
    if (loading?.distribution) {
      return <ChartLoadingState height={TREND_HEIGHT} shape="cartesian" />;
    }
    if (!nonCompliantBreakdown || nonCompliantBreakdown.total <= 0) {
      return (
        <ChartEmptyState
          height={TREND_HEIGHT}
          message="No data available"
          shape="cartesian"
        />
      );
    }
    return <NonCompliantDistributionPanel breakdown={nonCompliantBreakdown} />;
  };

  return (
    <div className="grid grid-cols-12 gap-3" data-testid="log-review-chart-grid">
      {/* Row 1 — Compliance status overview + Non-Compliant distribution */}
      <ChartCard
        title="Compliance Status Overview"
        className="col-span-12 lg:col-span-4"
      >
        <DonutPieChart
          {...LOG_REVIEW_DONUT_PROPS}
          name="log-review-compliance-status"
          data={withDonutColors(complianceStatus)}
          loading={loading?.complianceStatus}
          error={error?.complianceStatus ?? null}
          onRetry={onRetry}
          emptyMessage="No data available"
          ariaLabel="Compliance status overview"
        />
      </ChartCard>

      <ChartCard
        title="Non-Compliant Findings Distribution"
        subtitle={
          <span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
            Over the past 30 days
          </span>
        }
        className="col-span-12 lg:col-span-8"
      >
        {renderDistribution()}
      </ChartCard>

      {/* Row 2 — Findings trend (wide) + Workflow status (narrow) */}
      <ChartCard title="Findings Trend" className="col-span-12 lg:col-span-8">
        <AreaChart
          name="log-review-findings-trend"
          data={trend.rows}
          xKey="category"
          series={trend.series}
          variant="area"
          curve="monotone"
          showDots
          height={TREND_HEIGHT}
          loading={loading?.findingsTrend}
          error={error?.findingsTrend ?? null}
          onRetry={onRetry}
          emptyMessage="No data available"
          showLegend
          legendPosition="top"
          legendToggle
          valueFormatter={formatCompact}
          ariaLabel="Findings trend over time"
        />
      </ChartCard>

      <ChartCard
        title="Non-Compliant Workflow Status"
        className="col-span-12 lg:col-span-4"
      >
        <DonutPieChart
          {...LOG_REVIEW_DONUT_PROPS}
          name="log-review-workflow-status"
          data={withDonutColors(workflowStatus)}
          loading={loading?.workflowStatus}
          error={error?.workflowStatus ?? null}
          onRetry={onRetry}
          emptyMessage="No data available"
          ariaLabel="Non-Compliant workflow status"
        />
      </ChartCard>

      {/* Row 3 — Top-5 by Applications / Team / CR Categories */}
      <ChartCard
        title={chartTitleWithBadge("Non-Compliant Findings By Applications", "Top-5")}
        className="col-span-12 lg:col-span-4"
      >
        <RankedBarChart
          data={byApplications}
          valueLabel="Findings"
          loading={loading?.byApplications}
          error={error?.byApplications ?? null}
          onRetry={onRetry}
          ariaLabel="Non-Compliant findings by applications"
        />
      </ChartCard>

      <ChartCard
        title={chartTitleWithBadge("Non-Compliant Findings by Team", "Top-5")}
        className="col-span-12 md:col-span-6 lg:col-span-4"
      >
        <TreeMap
          data={byTeam}
          palette={TEAM_HEATMAP_COLORS}
          valueLabel="Findings"
          loading={loading?.byTeam}
          error={error?.byTeam ?? null}
          onRetry={onRetry}
          ariaLabel="Non-Compliant findings by team"
        />
      </ChartCard>

      <ChartCard
        title={chartTitleWithBadge("Non-Compliant Findings by CR Categories", "Top-5")}
        className="col-span-12 md:col-span-6 lg:col-span-4"
      >
        {/* No breakdown legend. On a ranked horizontal bar every category
            already names itself on the y-axis, so a label-only legend just
            prints the same words a second time -- and costs a row plus a
            pager to do it. Matches the Applications tile above. */}
        <RankedBarChart
          data={byCrCategories}
          valueLabel="Findings"
          loading={loading?.byCrCategories}
          error={error?.byCrCategories ?? null}
          onRetry={onRetry}
          ariaLabel="Non-Compliant findings by CR categories"
        />
      </ChartCard>
    </div>
  );
};
